import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SEO from '../utils/seo'
import { colors, textFieldSx, dialogPaperSx } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const Admin = () => {
  const [episodes, setEpisodes] = useState([])
  const [formData, setFormData] = useState({
    number: '',
    slug: '',
    title: '',
    date: '',
    duration: '',
    tags: '',
    prompt: '',
    summary: '',
    videoUrl: '',
    audioUrl: '',
    coverImage: ''
  })
  const [editing, setEditing] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, slug: null })

  useEffect(() => {
    fetchEpisodes()
  }, [])

  const fetchEpisodes = async () => {
    try {
      const response = await fetch(`${API_URL}/episodes`)
      const data = await response.json()
      setEpisodes(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching episodes:', error)
      setMessage({ type: 'error', text: 'Failed to fetch episodes' })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const episodeData = {
      ...formData,
      number: parseInt(formData.number),
      tags: formData.tags.split(',').map(t => t.trim()),
      showNotes: [{ type: 'p', text: formData.summary }]
    }

    try {
      const url = editing
        ? `${API_URL}/episodes/${editing}`
        : `${API_URL}/episodes`

      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(episodeData)
      })

      if (response.ok) {
        setMessage({
          type: 'success',
          text: editing ? 'Episode updated successfully!' : 'Episode created successfully!'
        })
        setFormData({
          number: '',
          slug: '',
          title: '',
          date: '',
          duration: '',
          tags: '',
          prompt: '',
          summary: '',
          videoUrl: '',
          audioUrl: '',
          coverImage: ''
        })
        setEditing(null)
        fetchEpisodes()
      } else {
        throw new Error('Failed to save episode')
      }
    } catch (error) {
      console.error('Error saving episode:', error)
      setMessage({ type: 'error', text: 'Failed to save episode' })
    }
  }

  const handleEdit = (episode) => {
    setFormData({
      number: episode.number.toString(),
      slug: episode.slug,
      title: episode.title,
      date: episode.date.split('T')[0],
      duration: episode.duration,
      tags: episode.tags.join(', '),
      prompt: episode.prompt,
      summary: episode.summary,
      videoUrl: episode.videoUrl || '',
      audioUrl: episode.audioUrl || '',
      coverImage: episode.coverImage || ''
    })
    setEditing(episode.slug)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/episodes/${deleteDialog.slug}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setMessage({ type: 'success', text: 'Episode deleted successfully!' })
        fetchEpisodes()
      } else {
        throw new Error('Failed to delete episode')
      }
    } catch (error) {
      console.error('Error deleting episode:', error)
      setMessage({ type: 'error', text: 'Failed to delete episode' })
    } finally {
      setDeleteDialog({ open: false, slug: null })
    }
  }

  return (
    <>
      <SEO title="Admin Console" />

      <div className="section">
        <div className="container-custom max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-off-white mb-8">
              Admin Console
            </h1>

            {message.text && (
              <Alert
                severity={message.type}
                onClose={() => setMessage({ type: '', text: '' })}
                sx={{ mb: 4 }}
              >
                {message.text}
              </Alert>
            )}

            {/* Episode Form */}
            <Card sx={{ backgroundColor: colors.bgCard, mb: 6 }}>
              <CardContent sx={{ p: 4 }}>
                <h2 className="text-2xl font-bold text-off-white mb-6">
                  {editing ? 'Edit Episode' : 'Add New Episode'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <TextField
                      fullWidth required label="Episode Number" name="number" type="number"
                      value={formData.number} onChange={handleChange} disabled={!!editing}
                      sx={textFieldSx}
                    />
                    <TextField
                      fullWidth required label="Slug (URL-friendly)" name="slug"
                      value={formData.slug} onChange={handleChange} disabled={!!editing}
                      sx={textFieldSx}
                    />
                  </div>

                  <TextField
                    fullWidth required label="Episode Title" name="title"
                    value={formData.title} onChange={handleChange}
                    sx={textFieldSx}
                  />

                  <TextField
                    fullWidth required label="Episode Prompt (The Key Question)" name="prompt"
                    multiline rows={2}
                    value={formData.prompt} onChange={handleChange}
                    sx={textFieldSx}
                  />

                  <TextField
                    fullWidth required label="Summary" name="summary"
                    multiline rows={3}
                    value={formData.summary} onChange={handleChange}
                    sx={textFieldSx}
                  />

                  <div className="grid md:grid-cols-3 gap-4">
                    <TextField
                      fullWidth required label="Date" name="date" type="date"
                      value={formData.date} onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldSx}
                    />
                    <TextField
                      fullWidth required label="Duration (e.g., 28:35)" name="duration"
                      value={formData.duration} onChange={handleChange}
                      sx={textFieldSx}
                    />
                    <TextField
                      fullWidth required label="Tags (comma-separated)" name="tags"
                      value={formData.tags} onChange={handleChange}
                      placeholder="genai, architecture, ux"
                      sx={textFieldSx}
                    />
                  </div>

                  <TextField
                    fullWidth label="YouTube URL" name="videoUrl"
                    value={formData.videoUrl} onChange={handleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    sx={textFieldSx}
                  />

                  <div className="flex gap-4">
                    <Button
                      type="submit" variant="contained" size="large"
                      sx={{
                        backgroundColor: colors.accent,
                        color: colors.textOnAccent,
                        fontWeight: 700,
                        '&:hover': { backgroundColor: colors.accentHover },
                      }}
                    >
                      {editing ? 'Update Episode' : 'Create Episode'}
                    </Button>

                    {editing && (
                      <Button
                        variant="outlined" size="large"
                        onClick={() => {
                          setEditing(null)
                          setFormData({
                            number: '', slug: '', title: '', date: '', duration: '',
                            tags: '', prompt: '', summary: '', videoUrl: '', audioUrl: '', coverImage: ''
                          })
                        }}
                        sx={{
                          borderColor: colors.secondaryLight,
                          color: colors.secondaryLight,
                          '&:hover': {
                            borderColor: colors.secondaryLight,
                            backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)',
                          },
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Episodes List */}
            <h2 className="text-2xl font-bold text-off-white mb-4">All Episodes</h2>
            <div className="space-y-4">
              {episodes.map(episode => (
                <Card key={episode.slug} sx={{ backgroundColor: colors.bgCard }}>
                  <CardContent sx={{ p: 3 }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <Chip
                            label={`EP ${episode.number.toString().padStart(2, '0')}`}
                            size="small"
                            sx={{
                              backgroundColor: colors.info,
                              color: colors.textOnAccent,
                              fontWeight: 700,
                            }}
                          />
                          <span className="text-gray-400 text-sm">{episode.date}</span>
                        </div>
                        <h3 className="text-lg font-bold text-off-white mb-1">
                          {episode.title}
                        </h3>
                        <p className="text-purple text-sm italic mb-2">
                          "{episode.prompt}"
                        </p>
                        <p className="text-gray-400 text-sm">{episode.summary}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <IconButton
                          onClick={() => handleEdit(episode)}
                          sx={{ color: colors.info }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setDeleteDialog({ open: true, slug: episode.slug })}
                          sx={{ color: colors.error }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {episodes.length === 0 && (
                <Card sx={{ backgroundColor: colors.bgCard }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <p className="text-gray-400">No episodes yet. Create your first one above!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, slug: null })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>Delete Episode?</DialogTitle>
        <DialogContent>
          <p className="text-gray-300">
            Are you sure you want to delete this episode? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, slug: null })}
            sx={{ color: colors.textMuted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{ color: colors.error, fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Admin
